import { useMemo, useState } from 'react';

import { useGetRunningTasksQuery } from '../../api/variantApi';
import type { RunningTask } from '../../variant_workflow/redux';
import { RUNNING_TASK_POLLING_INTERVAL } from '../constants';

export type RunningTaskStatusFilter = 'all' | RunningTask['status'];

type RunningTaskSearch = {
  freeText: string;
  errorText: string;
};

export const useRunningTasks = () => {
  const {
    data = [],
    isLoading,
    isSuccess,
  } = useGetRunningTasksQuery(undefined, {
    pollingInterval: RUNNING_TASK_POLLING_INTERVAL,
  });

  const [statusFilter, setStatusFilter] =
    useState<RunningTaskStatusFilter>('all');
  const [taskTypeFilter, setTaskTypeFilter] = useState('all');
  const [search, setSearch] = useState<RunningTaskSearch>({
    freeText: '',
    errorText: '',
  });

  const availableTaskTypes = useMemo(
    () => Array.from(new Set(data.map((task) => task.task_type))).sort(),
    [data],
  );

  const filteredTasks = useMemo(() => {
    const freeText = search.freeText.trim().toLowerCase();
    const errorText = search.errorText.trim().toLowerCase();

    return data.filter((task) => {
      if (statusFilter !== 'all' && task.status !== statusFilter) {
        return false;
      }
      if (taskTypeFilter !== 'all' && task.task_type !== taskTypeFilter) {
        return false;
      }
      if (freeText) {
        const haystack = [
          task.task_id,
          task.description,
          task.username,
          task.variant_hgvs ?? '',
          task.variant_id ? String(task.variant_id) : '',
        ]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(freeText)) {
          return false;
        }
      }
      if (
        errorText &&
        !(task.error_message ?? '').toLowerCase().includes(errorText)
      ) {
        return false;
      }
      return true;
    });
  }, [data, statusFilter, taskTypeFilter, search]);

  return {
    tasks: data,
    filteredTasks,
    isLoading,
    isSuccess,
    availableTaskTypes,
    statusFilter,
    setStatusFilter,
    taskTypeFilter,
    setTaskTypeFilter,
    search,
    setSearch,
  };
};
